import { useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { classes, style } from 'typestyle'
import moment from 'moment'

/* Context ====================================================================================== */
import { NavigationContext } from '../../__aeki__/contexts/navigation'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'

/* Hooks ======================================================================================== */
import useArray from '../../__aeki__/hooks/use-array'
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'
import useBoolean from '../../__aeki__/hooks/use-boolean'

/* Components =================================================================================== */
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Form, FormActions, FormContext } from '../../__aeki__/components/form'
import { Line } from '../../__aeki__/components/line'
import { Notification } from '../../__aeki__/components/notification'
import { Snippet, SnippetHead } from '../../__aeki__/modules/layouts/snippet'
import { Text } from '../../__aeki__/components/text/text.component'
import { Modal } from '../../__aeki__/components/modal'
import '../../modals/time'

/* Styles ======================================================================================= */
import { mr0md } from '../../__aeki__/styles/styleset/margin/mr0md'
import { paddingLeftInner } from '../../__aeki__/styles/default/padding'
import { pb8 } from '../../__aeki__/styles/styleset/padding/pb8'
import { pt8 } from '../../__aeki__/styles/styleset/padding/pt8'
import { f16i } from '../../__aeki__/styles/styleset/font-size/f16i'
import { mb4 } from '../../__aeki__/styles/styleset/margin/mb4'
import { mr4 } from '../../__aeki__/styles/styleset/margin/mr4'

export default addSnippet('CheckInAndOut', (props: any) => {
  const { row, sync } = props

  const { groupId, rowId } = useParams<any>()

  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/classes-children/${groupId}`
  const patchUrl = `${process.env.REACT_APP_API_URL}/a/groups/${groupId}/base/childcare-classes-dailies/table/${rowId}`

  const [data, $data] = useFetch({ data: [], count: 0 }, { url: apiUrl })
  const [patch, $patch] = useFetch({ data: [], count: 0 }, { url: patchUrl })
  const [toggled, $toggled] = useValue(false)
  const [students, $students] = useArray<any>([])
  const [attendance, $attendance] = useValue({})
  const [touched, $touched] = useBoolean(false)

  useEffect(() => {
    if (data.mounted && Object.keys(row).length) {
      $data
        .get({
          url: `${apiUrl}/${row.body.parent.body.program}`,
        })
        .then((payload: any) => {
          $students.set(row.body.students)
          $attendance.set(row?.body?.attendance)
        })
    }
  }, [data.mounted, row])

  return (
    <Snippet>
      <Form
        values={attendance}
        onChange={() => {
          $toggled.set(true)
        }}
        onSubmit={(data: any) => {
          $attendance.set(data)
          $toggled.set(false)
          $patch
            .patch({
              body: {
                replace: 'attendance',
                body: {
                  attendance: data,
                },
              },
            })
            .then(() => {
              sync()
              $touched.set(false)
              Notification.push({ label: 'Successfully updated!' })
            })
        }}
      >
        <SnippetHead
          title={`Attendance (${attendance?.attendance?.filter((a: any) => a.in)?.length ||
            0} / ${attendance?.attendance?.length || 0})`}
        >
          <Button
            label={touched ? 'Save' : 'Saved'}
            baseline={touched ? 'primary' : 'disabled'}
            size="sm"
            type={touched ? 'submit' : 'button'}
            square
          />
        </SnippetHead>
        <Line />
        <Box baseline="none" row>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <td
                className={classes(paddingLeftInner, pt8, pb8, style({ verticalAlign: 'middle' }))}
                style={{
                  borderBottom: '1px solid rgb(240,240,240)',
                }}
              >
                <Text size="sm" shrink baseline="surface" lighter>
                  Student Name
                </Text>
              </td>
              <td
                className={classes(paddingLeftInner, pt8, pb8, style({ verticalAlign: 'middle' }))}
                style={{
                  borderBottom: '1px solid rgb(240,240,240)',
                }}
              ></td>
            </thead>
            <tbody>
              <FormWrapper>
                {(form: any) => {
                  return data.payload.data
                    .filter((a: any) => students?.find?.((b: any) => b.id === a.id))
                    .map((client: any, key: any) => {
                      const { child } = client.body.generalInformation

                      return (
                        <tr key={key}>
                          <td
                            className={classes(
                              paddingLeftInner,
                              pt8,
                              pb8,
                              style({ verticalAlign: 'middle' }),
                            )}
                            style={{
                              borderBottom: '1px solid rgb(240,240,240)',
                            }}
                          >
                            <a
                              href={`/a/groups/${groupId}/base/childcare-clients/table/${client.id}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Text size="sm" link>
                                {child.fullName}{' '}
                              </Text>
                            </a>
                          </td>
                          <td
                            className={classes(
                              paddingLeftInner,
                              pt8,
                              pb8,
                              style({ verticalAlign: 'middle' }),
                            )}
                            style={{
                              borderBottom: '1px solid rgb(240,240,240)',
                            }}
                          >
                            <Box
                              flex
                              alignRight
                              alignCenterY
                              style={{
                                gap: 4,
                              }}
                            >
                              <Box baseline="none" row>
                                <Button
                                  className={mb4}
                                  size="md"
                                  label="Check in"
                                  square
                                  icon={
                                    <span className={classes('material-icons', f16i)}>
                                      {form.value.form[`attendance.${key}.in`]
                                        ? 'check_box'
                                        : 'check_box_outline_blank'}
                                    </span>
                                  }
                                  baseline={
                                    form.value.form[`attendance.${key}.in`]
                                      ? 'secondary'
                                      : 'background'
                                  }
                                  onClick={() => {
                                    $touched.set(true)
                                    form.dispatch({
                                      type: FormActions.SET,
                                      param: {
                                        value: !form.value.form[`attendance.${key}.in`],
                                        name: `attendance.${key}.in`,
                                      },
                                    })

                                    if (!form.value.form[`attendance.${key}.in`]) {
                                      form.dispatch({
                                        type: FormActions.SET,
                                        param: {
                                          value: new Date().toISOString(),
                                          name: `attendance.${key}.inTime`,
                                        },
                                      })
                                    } else {
                                      form.dispatch({
                                        type: FormActions.DELETE,
                                        param: {
                                          name: `attendance.${key}.inTime`,
                                        },
                                      })
                                      form.dispatch({
                                        type: FormActions.DELETE,
                                        param: {
                                          name: `attendance.${key}.outTime`,
                                        },
                                      })
                                      form.dispatch({
                                        type: FormActions.DELETE,
                                        param: {
                                          name: `attendance.${key}.out`,
                                        },
                                      })
                                    }

                                    form.dispatch({
                                      type: FormActions.SET,
                                      param: {
                                        value: false,
                                        name: `attendance.${key}.absent`,
                                      },
                                    })
                                  }}
                                />
                                <Box
                                  baseline="none"
                                  minHeight={17.5}
                                  onClick={() => {
                                    Modal.push({
                                      type: 'Time',
                                      componentProps: {
                                        value: form.value.form[`attendance.${key}.inTime`]
                                          ? moment(
                                              new Date(form.value.form[`attendance.${key}.inTime`]),
                                            ).format('HH:mm')
                                          : undefined,
                                        title: 'Change check-in time',
                                        subtitle: `${child.fullName}`,
                                        onSubmit: value => {
                                          form.dispatch({
                                            type: FormActions.SET,
                                            param: {
                                              value,
                                              name: `attendance.${key}.inTime`,
                                            },
                                          })
                                        },
                                      },
                                    })
                                  }}
                                >
                                  <Text size="xs" baseline="surface" className={mr4}>
                                    Time:
                                  </Text>
                                  <Text size="xs" baseline="surface" link={true}>
                                    {form.value.form[`attendance.${key}.inTime`]
                                      ? moment(
                                          new Date(form.value.form[`attendance.${key}.inTime`]),
                                        ).format('hh:mm a')
                                      : '-'}
                                  </Text>
                                </Box>
                              </Box>
                              <Box baseline="none" row>
                                <Button
                                  className={mb4}
                                  size="md"
                                  label="Check out"
                                  square
                                  icon={
                                    <span className={classes('material-icons', f16i)}>
                                      {form.value.form[`attendance.${key}.out`]
                                        ? 'check_box'
                                        : 'check_box_outline_blank'}
                                    </span>
                                  }
                                  baseline={
                                    form.value.form[`attendance.${key}.out`]
                                      ? 'secondary'
                                      : 'background'
                                  }
                                  onClick={() => {
                                    $touched.set(true)

                                    if (!form.value.form[`attendance.${key}.in`]) {
                                      form.dispatch({
                                        type: FormActions.SET,
                                        param: {
                                          value: true,
                                          name: `attendance.${key}.in`,
                                        },
                                      })
                                      form.dispatch({
                                        type: FormActions.SET,
                                        param: {
                                          value: new Date().toISOString(),
                                          name: `attendance.${key}.inTime`,
                                        },
                                      })
                                    }

                                    form.dispatch({
                                      type: FormActions.SET,
                                      param: {
                                        value: !form.value.form[`attendance.${key}.out`],
                                        name: `attendance.${key}.out`,
                                      },
                                    })

                                    if (!form.value.form[`attendance.${key}.out`]) {
                                      form.dispatch({
                                        type: FormActions.SET,
                                        param: {
                                          value: new Date().toISOString(),
                                          name: `attendance.${key}.outTime`,
                                        },
                                      })
                                    } else {
                                      form.dispatch({
                                        type: FormActions.DELETE,
                                        param: {
                                          name: `attendance.${key}.outTime`,
                                        },
                                      })
                                    }

                                    form.dispatch({
                                      type: FormActions.SET,
                                      param: {
                                        value: false,
                                        name: `attendance.${key}.absent`,
                                      },
                                    })
                                  }}
                                />
                                <Box
                                  baseline="none"
                                  flex
                                  minHeight={17.5}
                                  onClick={() => {
                                    Modal.push({
                                      type: 'Time',
                                      componentProps: {
                                        value: form.value.form[`attendance.${key}.outTime`]
                                          ? moment(
                                              new Date(
                                                form.value.form[`attendance.${key}.outTime`],
                                              ),
                                            ).format('HH:mm')
                                          : undefined,
                                        title: 'Change check-in time',
                                        subtitle: `${child.fullName}`,
                                        onSubmit: value => {
                                          form.dispatch({
                                            type: FormActions.SET,
                                            param: {
                                              value,
                                              name: `attendance.${key}.outTime`,
                                            },
                                          })
                                        },
                                      },
                                    })
                                  }}
                                >
                                  <Text size="xs" baseline="surface" className={mr4}>
                                    Time:
                                  </Text>
                                  <Text size="xs" baseline="surface" link={true}>
                                    {form.value.form[`attendance.${key}.outTime`]
                                      ? moment(
                                          new Date(form.value.form[`attendance.${key}.outTime`]),
                                        ).format('hh:mm a')
                                      : '-'}
                                  </Text>
                                </Box>
                              </Box>
                              <Box>
                                <Button
                                  size="md"
                                  label="Mark absent"
                                  icon={
                                    <span className={classes('material-icons', f16i)}>
                                      {form.value.form[`attendance.${key}.absent`]
                                        ? 'check_box'
                                        : 'check_box_outline_blank'}
                                    </span>
                                  }
                                  square
                                  className={classes(mr0md, mb4)}
                                  baseline={
                                    form.value.form[`attendance.${key}.absent`]
                                      ? 'secondary'
                                      : 'background'
                                  }
                                  onClick={() => {
                                    $touched.set(true)
                                    form.dispatch({
                                      type: FormActions.SET,
                                      param: {
                                        value: !form.value.form[`attendance.${key}.absent`],
                                        name: `attendance.${key}.absent`,
                                      },
                                    })
                                    form.dispatch({
                                      type: FormActions.SET,
                                      param: {
                                        value: false,
                                        name: `attendance.${key}.out`,
                                      },
                                    })
                                    form.dispatch({
                                      type: FormActions.SET,
                                      param: {
                                        value: false,
                                        name: `attendance.${key}.in`,
                                      },
                                    })
                                    form.dispatch({
                                      type: FormActions.DELETE,
                                      param: {
                                        name: `attendance.${key}.inTime`,
                                      },
                                    })
                                    form.dispatch({
                                      type: FormActions.DELETE,
                                      param: {
                                        name: `attendance.${key}.outTime`,
                                      },
                                    })
                                  }}
                                />
                                <Box baseline="none" flex minHeight={17.5}></Box>
                              </Box>
                            </Box>
                          </td>
                        </tr>
                      )
                    })
                }}
              </FormWrapper>
            </tbody>
          </table>
        </Box>
      </Form>
    </Snippet>
  )
})

const FormWrapper = (props: any) => {
  const { children } = props
  const form = useContext(FormContext)

  return children(form)
}
